import type { FormEventHandler, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface WindowContentProps {
  children: ReactNode;
  /** Kořenový element — `'div'` (default) nebo `'form'` (formulářové okno, Enter = submit). */
  as?: 'div' | 'form';
  /** Handler odeslání formuláře (jen `as="form"`). */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /** Připnutá patička pod obsahem (typicky `<FormActionBar/>` nebo prosté akce). `shrink-0`, mimo scroll. */
  footer?: ReactNode;
  /** Full-bleed pruh na horní hraně scroll regionu (bez paddingu). Jen `scroll` režim. */
  banner?: ReactNode;
  /**
   * `true` (default) = obsah je svislý scroll sloupec se standardním odsazením.
   * `false` = „fill" režim: `WindowContent` dá jen padded rám + patičku + výšku a **vnitřní layout i scrollbary
   * si řídí obsah sám** (2 sloupce, split, mapa, …).
   */
  scroll?: boolean;
  /** Jen scroll režim: `'full'` (default, panely přes celou šířku) | `'reading'` (max-w-220, vlevo). */
  width?: 'full' | 'reading';
  /** Extra třídy na obsahovou oblast. */
  className?: string;
  'data-testid'?: string;
}

/**
 * Sdílený rám obsahu okna agendy. Okno samo (`AgendaWindow`) renderuje obsah na doraz (bez paddingu), takže
 * jednotné odsazení (12px `p-phx-5`), scroll a připnutou patičku řeší tato komponenta — místo aby si každá
 * stránka opisovala `flex h-full min-h-0 flex-col` + `overflow-y-auto` kostur.
 *
 * - **scroll režim** (default): `banner` (full-bleed nahoře) + padded sloupec `children`, patička `footer`.
 * - **fill režim** (`scroll={false}`): jen padded oblast na celou výšku; vnitřní layout/scroll řídí `children`.
 */
export function WindowContent({
  children,
  as = 'div',
  onSubmit,
  footer,
  banner,
  scroll = true,
  width = 'full',
  className,
  'data-testid': testId,
}: WindowContentProps) {
  const body = (
    <>
      {scroll ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {banner}
          <div
            className={cn(
              'gap-phx-3 p-phx-5 flex w-full flex-col',
              width === 'reading' && 'max-w-220',
              className
            )}
          >
            {children}
          </div>
        </div>
      ) : (
        <div className={cn('p-phx-5 min-h-0 flex-1 overflow-hidden', className)}>{children}</div>
      )}
      {footer}
    </>
  );

  if (as === 'form') {
    return (
      <form className="flex h-full min-h-0 flex-col" onSubmit={onSubmit} data-testid={testId}>
        {body}
      </form>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={testId}>
      {body}
    </div>
  );
}
